import type { ReactNode } from "react";

import {
  AlertComponent,
  BadgeComponent,
  ButtonComponent,
  CardGridComponent,
  CustomComponentRenderer,
  DataTableComponent,
  FormattedDateComponent,
  InformationCardComponent,
  InlineStatusComponent,
  KeyValueListComponent,
  LinkComponent,
  MetricCardsComponent,
  StackComponent,
  TooltipComponent,
  TextComponent,
  TwoColumnComponent,
} from "./components";
import { TableToolbarComponent } from "./components/table-toolbar";
import type { RenderContext } from "./types";

import type {
  AlertSchemaProps,
  BadgeProps,
  ButtonProps,
  CardGridProps,
  CustomComponentSchemaProps,
  DataTableProps,
  FormattedDateProps,
  InformationCardProps,
  InlineStatusProps,
  KeyValueListProps,
  LinkProps,
  MetricCardsProps,
  TableToolbarProps,
  TextProps,
  TwoColumnProps,
  UiComponent,
} from "@/features/integrations/types";
import type { StackProps } from "@/features/integrations/types";
import type { TooltipProps } from "@/features/integrations/types";

/**
 * Recursively renders a UiComponent by matching component name to the
 * appropriate renderer, extracting typed props, and passing context.
 */
export function renderComponent(
  component: UiComponent,
  context: RenderContext
): ReactNode {
  if (!component?.component) {
    return null;
  }

  const renderChild = (child: UiComponent) => renderComponent(child, context);
  const renderWithContext = (
    child: UiComponent,
    contextOverride: Partial<RenderContext>
  ) => renderComponent(child, { ...context, ...contextOverride });

  const baseProps = {
    renderChild,
    renderWithContext,
    executeAction: context.executeAction,
    data: {
      ...context.data,
      clientId: context.clientId, // Include clientId in data for components that need it
    },
    integrationName: context.integrationName,
    moduleName: context.moduleName,
    clientId: context.clientId,
    tenantId: context.tenantId,
  };

  switch (component.component) {
  case "text": {
    const props = component.props as unknown as TextProps;
    return <TextComponent {...baseProps} {...props} />;
  }

  case "badge": {
    const props = component.props as unknown as BadgeProps;
    return <BadgeComponent {...baseProps} {...props} />;
  }

  case "button": {
    const props = component.props as unknown as ButtonProps;
    return <ButtonComponent {...baseProps} {...props} />;
  }

  case "link": {
    const props = component.props as unknown as LinkProps;
    return <LinkComponent {...baseProps} {...props} />;
  }

  case "formattedDate": {
    const props = component.props as unknown as FormattedDateProps;
    return <FormattedDateComponent {...baseProps} {...props} />;
  }

  case "twoColumn": {
    const props = component.props as unknown as TwoColumnProps;
    return <TwoColumnComponent {...baseProps} {...props} />;
  }

  case "informationCard": {
    const props = component.props as unknown as InformationCardProps;
    return <InformationCardComponent {...baseProps} {...props} />;
  }

  case "stack": {
    const props = component.props as unknown as StackProps;
    return <StackComponent {...baseProps} {...props} />;
  }

  case "tooltip": {
    const props = component.props as unknown as TooltipProps;
    return <TooltipComponent {...baseProps} {...props} />;
  }

  case "metricCards": {
    const props = component.props as unknown as MetricCardsProps;
    return (
      <MetricCardsComponent
        {...baseProps}
        {...props}
      />
    );
  }

  case "inlineStatus": {
    const props = component.props as unknown as InlineStatusProps;
    return <InlineStatusComponent {...baseProps} {...props} />;
  }

  case "keyValueList": {
    const props = component.props as unknown as KeyValueListProps;
    return <KeyValueListComponent {...baseProps} {...props} />;
  }

  case "cardGrid": {
    const props = component.props as unknown as CardGridProps;
    return (
      <CardGridComponent
        {...baseProps}
        {...props}
        items={context.items ?? context.tableData ?? []}
        isLoading={context.isLoading ?? false}
        pagination={context.pagination}
        infiniteScroll={context.infiniteScroll}
        onItemClick={context.onItemClick ?? context.onRowClick}
        isItemClickable={context.isItemClickable}
      />
    );
  }

  case "dataTable": {
    const props = component.props as unknown as DataTableProps;
    return (
      <DataTableComponent
        {...baseProps}
        {...props}
        tableData={context.tableData ?? []}
        isLoading={context.isLoading ?? false}
        pagination={context.pagination}
        onSort={context.onSort}
        onRowClick={context.onRowClick}
        orderBy={context.orderBy}
        descending={context.descending}
        emptyMessage={context.emptyMessage}
        customCellRenderers={context.customCellRenderers}
        accessorMap={context.accessorMap}
      />
    );
  }

  case "tableToolbar": {
    const props = (component.props ?? {}) as TableToolbarProps;
    return (
      <TableToolbarComponent
        {...baseProps}
        {...props}
        searchValue={context.searchValue}
        onSearchChange={context.onSearchChange}
        filterValues={context.filterValues}
        onFilterChange={context.onFilterChange}
        onFiltersClick={context.onFiltersClick}
        customToolbarActions={context.customToolbarActions}
        dateRangePickerOptions={context.dateRangePickerOptions}
      />
    );
  }

  case "alert": {
    const props = component.props as unknown as AlertSchemaProps;
    return <AlertComponent {...baseProps} {...props} />;
  }

  case "custom": {
    const props = component.props as unknown as CustomComponentSchemaProps;
    return <CustomComponentRenderer {...baseProps} {...props} />;
  }

  default:
    return null;
  }
}
